function extractContactName(contact) {
  if (!contact) return "";
  const identity = contact.identity || {};
  const person = identity.person || {};
  const first = (person.firstName || contact.firstName || "").trim();
  const last = (person.lastName || contact.lastName || "").trim();
  const full = `${first} ${last}`.trim();
  return full || identity.company?.name || contact.company || "";
}

function extractCompanyContext(contact) {
  if (!contact) return "";
  const identity = contact.identity || {};
  const company = identity.company || {};
  const person = identity.person || {};
  const lines = [];

  const companyName =
    (typeof company.name === "string" && company.name.trim())
    || (typeof contact.company === "string" && contact.company.trim())
    || "";
  if (companyName) lines.push(`Company name: ${companyName}`);

  const companyType =
    (typeof company.type === "string" && company.type.trim())
    || (typeof contact.companyType === "string" && contact.companyType.trim())
    || "";
  if (companyType) lines.push(`Company type: ${companyType}`);

  const personTitle =
    (typeof person.title === "string" && person.title.trim())
    || (typeof contact.title === "string" && contact.title.trim())
    || "";
  if (personTitle) lines.push(`Recipient title: ${personTitle}`);

  const website =
    (typeof company.website === "string" && company.website.trim())
    || (typeof contact.website === "string" && contact.website.trim())
    || "";
  if (website) lines.push(`Company website: ${website}`);

  return lines.join("\n");
}

function describeVoice(voiceProfile) {
  if (!voiceProfile) return "";
  const lines = [];
  if (voiceProfile.name) lines.push(`Mode: ${voiceProfile.name}`);
  if (voiceProfile.toneArchetype) lines.push(`Tone: ${voiceProfile.toneArchetype}`);
  if (voiceProfile.formality) lines.push(`Formality: ${voiceProfile.formality}`);
  if (voiceProfile.customInstructions) {
    lines.push(`Custom instructions:\n${voiceProfile.customInstructions}`);
  }
  if (voiceProfile.signaturePhrases?.length) {
    lines.push(`Signature phrases: ${voiceProfile.signaturePhrases.join(", ")}`);
  }
  if (voiceProfile.avoidPhrases?.length) {
    lines.push(`Avoid: ${voiceProfile.avoidPhrases.join(", ")}`);
  }
  const sampleText =
    (typeof voiceProfile.sampleText === "string" && voiceProfile.sampleText.trim())
      || (typeof voiceProfile.voiceSample === "string" && voiceProfile.voiceSample.trim())
      || (Array.isArray(voiceProfile.voiceSamples) ? voiceProfile.voiceSamples.filter(Boolean).join("\n") : "");
  if (sampleText) {
    lines.push(`Sample of how the user writes:\n${sampleText}`);
  }
  return lines.join("\n");
}

function modeSpecificRules(voiceProfile) {
  const modeId = String(voiceProfile?.id || "").toLowerCase();
  const modeName = String(voiceProfile?.name || "").toLowerCase();
  const key = `${modeId} ${modeName}`;

  if (key.includes("cold") || key.includes("intro")) {
    return [
      "- This is a true cold intro: do not imply any prior relationship, previous conversation, or ongoing project.",
      "- Do NOT use phrases like 'touch base', 'following up', 'as discussed', 'great chatting', or 'circle back'.",
      "- State clearly who the sender is, why they are reaching out, and one specific call-to-action.",
    ].join("\n");
  }

  if (key.includes("follow")) {
    return [
      "- This is a follow-up: briefly reference prior context and ask for a simple next step.",
      "- Keep the callback short and explicit.",
    ].join("\n");
  }

  if (key.includes("reminder")) {
    return [
      "- This is a reminder: be direct, concrete, and time-specific when time/date is provided.",
      "- Avoid sales-style outreach language.",
    ].join("\n");
  }

  if (key.includes("thank")) {
    return [
      "- This is a thank-you message: lead with gratitude and include one concrete reason.",
      "- Keep the ask minimal or optional.",
    ].join("\n");
  }

  return "";
}

export function assembleSmsPrompt({ voiceProfile, contact, purpose, companyKnowledge, senderFirstName }) {
  const contactName = extractContactName(contact) || "the recipient";
  const voiceDescription = describeVoice(voiceProfile);
  const companyContext = extractCompanyContext(contact);
  const modeRules = modeSpecificRules(voiceProfile);

  const system = [
    "You are drafting a SHORT SMS text message on behalf of the user.",
    "Constraints (strictly follow):",
    "- The output MUST be the SMS body only — no subject line, no greeting block, no signature.",
    "- Target 180-260 characters for richer context when possible. Never exceed 320 characters.",
    "- Plain text only. No markdown, no quotes around the message, no bullet points.",
    "- Do NOT invent facts about the recipient or sender. Only use what is provided.",
    "- Use sender company knowledge only when provided in the SENDER COMPANY KNOWLEDGE section.",
    "- Recipient company context is about the audience and must not be treated as the sender's company identity.",
    "- Match the tone described below as closely as possible.",
    senderFirstName ? `- You may sign off informally with "${senderFirstName}" only if the tone calls for it; otherwise, no signature.` : "- Do not add a signature.",
  ].join("\n");

  const userParts = [
    `Recipient: ${contactName}`,
    companyKnowledge?.trim()
      ? `\nSENDER COMPANY KNOWLEDGE (ABOUT THE USER/SENDER):\n${companyKnowledge.trim()}`
      : "\nSENDER COMPANY KNOWLEDGE:\nNot provided. Do not mention sender company facts.",
    companyContext ? `\nCOMPANY CONTEXT:\n${companyContext}` : "",
    voiceDescription ? `\nVOICE / TONE TO MATCH:\n${voiceDescription}` : "",
    modeRules ? `\nMODE-SPECIFIC RULES:\n${modeRules}` : "",
    `\nPURPOSE / WHAT TO SAY:\n${purpose || "Send a brief friendly text."}`,
    "\nWrite the SMS body now. Output the message text only — nothing else.",
  ].filter(Boolean);

  return { system, user: userParts.join("\n") };
}

export const SMS_GENERATION_CONFIG = {
  temperature: 0.75,
  top_p: 0.9,
  max_tokens: 220,
  repetition_penalty: 1.1,
};

export async function generateSmsWithWebLLM(engine, params, onChunk) {
  const { system, user } = assembleSmsPrompt(params);

  const messages = [
    { role: "system", content: system },
    { role: "user", content: user },
  ];

  let full = "";
  let usage = null;

  const chunks = await engine.chat.completions.create({
    messages,
    ...SMS_GENERATION_CONFIG,
    stream: true,
    stream_options: { include_usage: true },
  });

  for await (const chunk of chunks) {
    const delta = chunk.choices?.[0]?.delta?.content || "";
    if (delta) {
      full += delta;
      if (onChunk) onChunk(delta, full);
    }
    if (chunk.usage) usage = chunk.usage;
  }

  return { text: full, usage };
}
